import React, {Dispatch, useEffect, useState} from 'react';

import {format} from 'date-fns';
import {useTranslation} from 'react-i18next';

import {getExtendedCert} from 'api/registration';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Modal} from 'components/ui-kit/Modal';
import {Spinner} from 'components/ui-kit/Spinner';
import {history} from 'core/history';
import {useCryptoPro} from 'core/hooks/cryptoPro';
import {ROUTE} from 'core/routes';
import {cleanRegisterData, setCert} from 'models/register';
import {CryptoProCert} from 'types/plugins/cadesPlugin';

import {CertMockModal} from '../CertMockModal';

import {
    CancelButton,
    ContentWrapper,
    ControlsWrapper,
    List,
    ListItem,
    ListWrapper,
    LoadingWrapper,
    SpinnerLayer,
} from './styled';

export function CertRegisterModal({show, setShow}: {show: boolean; setShow: Dispatch<boolean>}) {
    const LOCALE_CERTS_PATH = 'pages.login.certs.';
    const {t} = useTranslation();
    const cryptoPro = useCryptoPro();

    const [showMock, setShowMock] = useState(false);

    const [loading, setLoading] = useState(false);
    const [selectedCert, setSelectedCert] = useState<CryptoProCert | null>(null);
    const [certsList, setCertsList] = React.useState<CryptoProCert[]>([]);
    const getCertsList = async () => {
        try {
            setLoading(true);
            const raw: CryptoProCert[] | undefined = await cryptoPro.getCertificatesInfo();
            if (!raw) return;
            setCertsList(raw);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    // const [currentOrganization, setCurrentOrganization] = useState<CertLegalPerson | null>(null);
    // const [selectedOrganization, setSelectedOrganization] = useState<CertLegalPerson | null>(null);
    // const [organizationsList, setOrganizationsList] = useState<CertLegalPerson[]>([]);
    // const getOrganization = async (cert: CryptoProCert) => {
    //     try {
    //         setLoadingOrganizations(true);
    //         const extendedCert = (await getExtendedCert(cert)).data;
    //         const legalPersons = (await getLegalPersons(extendedCert)).data;
    //         setCert(extendedCert);
    //         setOrganizationsList(legalPersons.organizations);
    //     } catch (error) {
    //         toast.error(error.message);
    //         console.error(error);
    //     } finally {
    //         setLoadingOrganizations(false);
    //     }
    // };

    // const currentStep = useMemo(() => {
    //     if (organizationsList.length) return 'organizations';
    //     return 'certs';
    // }, [organizationsList]);

    useEffect(() => {
        getCertsList();
    }, []);

    const onSelectCert = async () => {
        try {
            if (!selectedCert) return;
            const extendedCert = (await getExtendedCert(selectedCert)).data;
            setCert(extendedCert);
            history.push(ROUTE.REGISTER);
        } catch (error) {
            console.error(error);
        }
    };

    // const onSelectOrganization = () => {
    //     if (!selectedOrganization) return;
    //     setOrganization(selectedOrganization);
    //     history.push(ROUTE.REGISTER);
    // };

    const onClear = () => {
        cleanRegisterData();
        setSelectedCert(null);
        // setSelectedOrganization(null);
    };

    const onClose = () => {
        setShow(false);
        onClear();
    };

    // const onBack = () => {
    //     setOrganization(null);
    // };

    const onRefresh = () => {
        onClear();
        getCertsList();
    };

    const Loading = () => (
        <LoadingWrapper>
            <p>{t(`${LOCALE_CERTS_PATH}load`)}</p>
            <CancelButton onClick={onClose}>{t(LOCALE_CERTS_PATH + 'cancel')}</CancelButton>
            <SpinnerLayer>
                <Spinner />
            </SpinnerLayer>
        </LoadingWrapper>
    );

    const CertsList = () => (
        <ListWrapper>
            <List>
                {certsList.map((cert: CryptoProCert) => (
                    <ListItem
                        selected={selectedCert?.thumbprint === cert.thumbprint}
                        onClick={() => setSelectedCert(cert)}
                        key={cert.thumbprint}
                    >
                        <div>
                            <span>{t(LOCALE_CERTS_PATH + 'issuer')}:</span> {cert.issuer_name}
                        </div>
                        <div>
                            <span>{t(LOCALE_CERTS_PATH + 'subject')}:</span> {cert.subject_name}
                        </div>
                        <div>
                            <span>{t(LOCALE_CERTS_PATH + 'valid')}: </span>
                            {`${format(new Date(cert.valid_from_date), 'dd.MM.yyyy')} - ${format(
                                new Date(cert.valid_to_date),
                                'dd.MM.yyyy',
                            )}`}
                        </div>
                    </ListItem>
                ))}
            </List>
            <ControlsWrapper>
                <Button variant={ButtonVariant.Outline} disabled={loading} onClick={onClose}>
                    {t(LOCALE_CERTS_PATH + 'cancel')}
                </Button>
                <Button disabled={loading} onClick={() => setShowMock(true)}>
                    Select mock
                </Button>
                <Button disabled={loading} onClick={onRefresh}>
                    {t(LOCALE_CERTS_PATH + 'refresh')}
                </Button>
                <Button disabled={!selectedCert || loading} onClick={onSelectCert}>
                    {t(LOCALE_CERTS_PATH + 'select')}
                </Button>
            </ControlsWrapper>
        </ListWrapper>
    );

    // const OrganizationsList = () => (
    //     <ListWrapper>
    //         <List>
    //             {organizationsList?.map((org: CertLegalPerson) => (
    //                 <ListItem
    //                     selected={selectedOrganization?.tin === org.tin}
    //                     onClick={() => setSelectedOrganization(org)}
    //                     key={org.tin}
    //                 >
    //                     <div>
    //                         <span>{t(LOCALE_ORG_PATH + 'name')}:</span> {org.company_name}
    //                     </div>
    //                     <div>
    //                         <span>{t(LOCALE_ORG_PATH + 'legal')}:</span> {org.legal_address}
    //                     </div>
    //                     <div>
    //                         <span>{t(LOCALE_ORG_PATH + 'tin')}:</span> {org.tin}
    //                     </div>
    //                 </ListItem>
    //             ))}
    //         </List>
    //         <ControlsWrapper>
    //             <Button
    //                 variant={ButtonVariant.Outline}
    //                 disabled={loadingOrganizations}
    //                 onClick={onClose}
    //             >
    //                 {t(LOCALE_ORG_PATH + 'cancel')}
    //             </Button>
    //             <Button disabled={loadingOrganizations} onClick={onBack}>
    //                 {t(LOCALE_ORG_PATH + 'back')}
    //             </Button>
    //             <Button disabled={!selectedOrganization} onClick={onSelectOrganization}>
    //                 {t(LOCALE_ORG_PATH + 'select')}
    //             </Button>
    //         </ControlsWrapper>
    //     </ListWrapper>
    // );

    const Content = () => (loading ? <Loading /> : <CertsList />);

    return (
        <Modal title={t(`${LOCALE_CERTS_PATH}title`)} show={show} onClose={() => setShow(false)}>
            <CertMockModal
                show={showMock}
                setShow={setShowMock}
                addCert={(item) => setCertsList((items) => [...items, item])}
            />
            <ContentWrapper>
                <Content />
            </ContentWrapper>
        </Modal>
    );
}
